import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";
import { type ColumnDef } from "@tanstack/react-table";
import {
  FolderOpen,
  Target,
  CheckCircle2,
  AlertCircle,
  Plus,
  ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardContent,
} from "@/components/ui/Card";
import { DataTable } from "@/components/ui/DataTable";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CaseStats {
  totalCases: number;
  openCases: number;
  onHoldCases: number;
  totalGoals: number;
  inProgressGoals: number;
  completedGoals: number;
}

interface AgencyCaseRow {
  id: number;
  person_id: number;
  description: string | null;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  category_name?: string;
  author_first_name?: string;
  author_last_name?: string;
  client_first_name?: string;
  client_last_name?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const statusColors: Record<string, string> = {
  open: "bg-green-100 text-green-800",
  in_progress: "bg-blue-100 text-blue-800",
  closed: "bg-gray-100 text-gray-800",
  on_hold: "bg-amber-100 text-amber-800",
};

const statusFilterOptions = [
  { value: "all", label: "All Statuses" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "closed", label: "Closed" },
  { value: "on_hold", label: "On Hold" },
];

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

const columns: ColumnDef<AgencyCaseRow, unknown>[] = [
  {
    accessorKey: "id",
    header: "ID",
    cell: ({ row }) => (
      <span className="font-mono text-xs text-muted-foreground">
        #{row.original.id}
      </span>
    ),
    size: 60,
  },
  {
    id: "client",
    header: "Client",
    accessorFn: (row) =>
      row.client_first_name && row.client_last_name
        ? `${row.client_first_name} ${row.client_last_name}`
        : `Client #${row.person_id}`,
    cell: ({ row }) => {
      const name =
        row.original.client_first_name && row.original.client_last_name
          ? `${row.original.client_first_name} ${row.original.client_last_name}`
          : `Client #${row.original.person_id}`;
      return (
        <Link
          to={`/clients/${row.original.person_id}`}
          className="text-primary hover:underline font-medium"
        >
          {name}
        </Link>
      );
    },
  },
  {
    accessorKey: "category_name",
    header: "Category",
    cell: ({ getValue }) => (
      <span className="text-sm">
        {(getValue() as string) ?? "General"}
      </span>
    ),
  },
  {
    accessorKey: "description",
    header: "Description",
    cell: ({ getValue }) => {
      const desc = getValue() as string | null;
      if (!desc) return <span className="text-muted-foreground">-</span>;
      return (
        <span className="text-sm line-clamp-1" title={desc}>
          {desc}
        </span>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ getValue }) => {
      const status = (getValue() as string) ?? "open";
      return (
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
            statusColors[status] ?? statusColors.open,
          )}
        >
          {status.replace("_", " ")}
        </span>
      );
    },
  },
  {
    id: "staff",
    header: "Staff",
    accessorFn: (row) =>
      row.author_first_name && row.author_last_name
        ? `${row.author_first_name} ${row.author_last_name}`
        : "",
    cell: ({ getValue }) => {
      const name = getValue() as string;
      return name ? (
        <span className="text-sm">{name}</span>
      ) : (
        <span className="text-muted-foreground">-</span>
      );
    },
  },
  {
    accessorKey: "start_date",
    header: "Started",
    cell: ({ getValue }) => {
      const date = getValue() as string | null;
      return date ? (
        <span className="text-sm">
          {new Date(date).toLocaleDateString()}
        </span>
      ) : (
        <span className="text-muted-foreground">-</span>
      );
    },
  },
  {
    id: "actions",
    header: "",
    cell: ({ row }) => (
      <Link to={`/cases/${row.original.id}`}>
        <Button variant="ghost" size="icon">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </Link>
    ),
    size: 40,
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CaseManagementPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: statsData, isLoading: statsLoading } = useQuery<{
    data: CaseStats;
  }>({
    queryKey: ["case-stats"],
    queryFn: () => api.get<{ data: CaseStats }>("/cases/stats"),
  });

  const { data: casesData, isLoading: casesLoading } = useQuery<{
    data: AgencyCaseRow[];
  }>({
    queryKey: ["all-cases", statusFilter],
    queryFn: () =>
      api.get<{ data: AgencyCaseRow[] }>("/cases", {
        params: statusFilter !== "all" ? { status: statusFilter } : undefined,
      }),
  });

  const stats = statsData?.data;
  const cases = casesData?.data ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Case Management
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage all cases across your agency
          </p>
        </div>
        <Button className="gap-2" onClick={() => void navigate("/clients")}>
          <Plus className="h-4 w-4" />
          New Case
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          title="Open Cases"
          value={stats?.openCases ?? 0}
          icon={FolderOpen}
          color="text-blue-600"
          bgColor="bg-blue-50"
          isLoading={statsLoading}
        />
        <SummaryCard
          title="Cases On Hold"
          value={stats?.onHoldCases ?? 0}
          icon={AlertCircle}
          color="text-amber-600"
          bgColor="bg-amber-50"
          isLoading={statsLoading}
        />
        <SummaryCard
          title="Goals In Progress"
          value={stats?.inProgressGoals ?? 0}
          icon={Target}
          color="text-purple-600"
          bgColor="bg-purple-50"
          isLoading={statsLoading}
        />
        <SummaryCard
          title="Goals Completed"
          value={stats?.completedGoals ?? 0}
          icon={CheckCircle2}
          color="text-green-600"
          bgColor="bg-green-50"
          isLoading={statsLoading}
        />
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-muted-foreground">
          Filter by status:
        </label>
        <div className="flex gap-1">
          {statusFilterOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                statusFilter === opt.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Cases table */}
      <DataTable
        columns={columns}
        data={cases}
        isLoading={casesLoading}
        searchColumn="client"
        searchPlaceholder="Search by client name..."
        pageSize={20}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary Card
// ---------------------------------------------------------------------------

function SummaryCard({
  title,
  value,
  icon: Icon,
  color,
  bgColor,
  isLoading,
}: {
  title: string;
  value: number;
  icon: typeof FolderOpen;
  color: string;
  bgColor: string;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "flex h-10 w-10 items-center justify-center rounded-lg",
              bgColor,
            )}
          >
            <Icon className={cn("h-5 w-5", color)} />
          </div>
          <div>
            {isLoading ? (
              <div className="h-7 w-12 animate-pulse rounded bg-muted" />
            ) : (
              <p className="text-2xl font-bold text-foreground">{value}</p>
            )}
            <p className="text-xs text-muted-foreground">{title}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
